<script setup lang="ts">
import { ref, onMounted, watch } from 'vue'
import LoginPage from '~/pages/login.vue'
import { useTagStore } from '@/stores/tags'
import IconPrompt from '@/assets/icons/IconPrompt.vue'
import IconModel  from '@/assets/icons/IconModels.vue'
import IconPWA    from '@/assets/icons/IconPWA.vue'
import Logo       from '/rana-logo.png'
import IconHome from '@/assets/icons/IconHome.vue'
import IconDiscover from '~/assets/icons/IconDiscover.vue'
import IconProducts from '~/assets/icons/IconProducts.vue'
import { useNuxtApp } from '#app'

const { $vm: vm } = useNuxtApp()
const tagStore = useTagStore()
const isMobileMenuOpen = ref(false)

const toggleMobileMenu = () => {
  isMobileMenuOpen.value = !isMobileMenuOpen.value
}

const emit = defineEmits<{
  (e: 'open-login-panel'): void
}>()

const openLoginPanel = function () {
  emit('open-login-panel')
}

// derive active view from selected tags
const activeView = ref<'Home' | 'Prompt' | 'Models' |'Products' | 'Web Apps'>('Home')

function determineActiveView(sel: string[]): 'Home' | 'Prompt' | 'Models' | 'Products' | 'Web Apps' {
  if (sel.includes('Prompt'))      return 'Prompt'
  else if (sel.includes('Models'))   return 'Models'
  else if (sel.includes('Products')) return 'Products'
  else if (sel.includes('Web Apps')) return 'Web Apps'
  else                               return 'Home'
}

watch(
  () => tagStore.selected.slice(),
  (sel) => {
    activeView.value = determineActiveView(sel)
  },
  { immediate: false }
)

function selectView(view: 'Home' | 'Prompt' | 'Models' | 'Web Apps') {
  tagStore.unselectAll()
  tagStore.select(view)
  
  isMobileMenuOpen.value = false
}

onMounted(() => {
  setTimeout(() => {
    const sel = tagStore.selected
    const viewToSelect = determineActiveView(sel)
    selectView(viewToSelect)
  }, 100)
})

</script>

<template>
  <div class="relative">
    <header class="w-full bg-[hsl(var(--background))] flex items-center">
      <section class="w-full flex rounded-lg bg-neutral px-2 h-20">
        <NuxtLink to="/" class="self-start pt-2">
          <img :src="Logo" alt="Rana Logo" class="h-[60px] w-[60px] mb-4" />
        </NuxtLink>
        <!-- Burger Menu Button (shows on small screens) -->
        <div class="lg:hidden ml-2 flex items-center">
          <button @click="toggleMobileMenu" class="text-[#00ccaa] p-2">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
                    :d="isMobileMenuOpen ? 'M6 18L18 6M6 6l12 12' : 'M4 6h16M4 12h16M4 18h16'" />
            </svg>
          </button>
        </div>

        <div class="hidden lg:flex justify-center items-center h-full space-x-2 ml-4">
          <!-- Desktop menu items remain the same -->
          <!-- Home -->
          <div class="relative group cursor-pointer"
              @click="selectView('Home')" tabindex="0"  @keyup.enter="selectView('Home')">
            <IconDiscover
              class="border-[#00ccaa] border-1 w-12 h-12 p-2 rounded-lg "
              :class="activeView == 'Home'
                      ? 'bg-[#64F4D5] text-neutral'
                      : 'bg-neutral text-[#00ccaa]'"
              :selected="activeView == 'Home'"
            />
            <div 
            class="absolute left-1/2 top-full mt-2 hidden
                        group-hover:block
                        bg-[hsl(var(--accent))] text-neutral text-xs
                        rounded-md px-2 py-1 whitespace-nowrap
                        transform -translate-x-1/2 z-10">
              Home
            </div>
          </div>

          <!-- Prompts -->
          <div 
          class="relative group cursor-pointer"
             tabindex="0"  @click="selectView('Prompt')"  @keyup.enter="selectView('Prompt')">
            <IconPrompt
              class="border-[#00ccaa] border-1 w-12 h-12 p-1 rounded-lg"
              :class="activeView === 'Prompt'
                      ? 'bg-[#64F4D5] text-neutral'
                      : 'bg-neutral text-[#00ccaa]'"
              :selected="activeView === 'Prompt'"
            />
            <div 
            class="absolute left-1/2 top-full mt-2 hidden
                        group-hover:block
                        bg-[hsl(var(--accent))] text-neutral text-xs
                        rounded-md px-2 py-1 whitespace-nowrap
                        transform -translate-x-1/2 z-10">
              Prompts
            </div>
          </div>
 <!-- Web Apps -->
          <div 
          class="relative group cursor-pointer" tabindex="0" 
              @click="selectView('Web Apps')"
              @keyup.enter="selectView('Web Apps')">
            <IconPWA
              class="border-[#00ccaa] border-1 w-12 h-12 p-1 rounded-lg"
              :class="activeView === 'Web Apps'
                      ? 'bg-[#64F4D5] text-neutral'
                      : 'bg-neutral text-[#00ccaa]'"
              :selected="activeView === 'Web Apps'"
            />
            <div 
            class="absolute left-1/2 top-full mt-2 hidden
                        group-hover:block
                        bg-[hsl(var(--accent))] text-neutral text-xs
                        rounded-md px-2 py-1 whitespace-nowrap
                        transform -translate-x-1/2 z-10">
              Web Apps
            </div>
          </div>

          <!-- Models -->
          <div 
          class="relative group cursor-pointer"
              tabindex="0"   @click="selectView('Models')" @keyup.enter="selectView('Models')">
            <IconModel
              class="border-[#00ccaa] border-1 w-12 h-12 p-2 rounded-lg"
              :class="activeView === 'Models'
                      ? 'bg-[#64F4D5] text-neutral'
                      : 'bg-neutral text-[#00ccaa]'"
              :selected="activeView === 'Models'"
            />
            <div 
            class="absolute left-1/2 top-full mt-2 hidden
                        group-hover:block
                        bg-[hsl(var(--accent))] text-neutral text-xs
                        rounded-md px-2 py-1 whitespace-nowrap
                        transform -translate-x-1/2 z-10">
              Models
            </div>
          </div>

         
          <!-- Products -->
          <div 
          class="relative group cursor-pointer"  
            tabindex="0"
            @click="selectView('Products')" 
            @keyup.enter="selectView('Products')">
            <IconProducts
              class="border-[#00ccaa] border-1 w-12 h-12 p-1 rounded-lg"
              :class="activeView === 'Products'
                      ? 'bg-[#64F4D5] text-neutral'
                      : 'bg-neutral text-[#00ccaa]'"
              :selected="activeView === 'Products'"
            />
            <div 
            class="absolute left-1/2 top-full mt-2 hidden
                        group-hover:block
                        bg-[hsl(var(--accent))] text-neutral text-xs
                        rounded-md px-2 py-1 whitespace-nowrap
                        transform -translate-x-1/2 z-10">
              Products
            </div>
          </div>






        </div>

        <div class="flex-1 flex items-top">
          <div class="flex-1 h-[2px] bg-gradient-to-r from-transparent to-[#00ccaa] mt-6"></div>
          <span class="mx-4 whitespace-nowrap sm:text-2xl font-bold flex h-12 p-4 items-center overflow-clip rounded-lg border-primary text-info">
            Rana Marketplace
          </span>
          <div class="flex-1 h-[2px] bg-gradient-to-l from-transparent to-[#00ccaa] mt-6"></div>
        </div>
      </section>

      <div class="hidden sm:flex items-center justify-end sm:ml-2 h-20">
        <LoginPage @open-login-panel="openLoginPanel" />
      </div>
    </header>

    <!-- Mobile Menu Overlay (fixed position) -->
    <div v-if="isMobileMenuOpen" 
         class="fixed inset-0 bg-black/70 bg-opacity-50 z-50 lg:hidden  transition-opacity duration-300 ease-in-out"
         @click="isMobileMenuOpen = false">
         
      <!-- Mobile Menu Panel -->
      <div class="absolute top-0 left-0 w-64 h-screen bg-[hsl(var(--background))] shadow-lg p-4 
              transform transition-transform duration-300 ease-in-out"
       :class="isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'"
           @click.stop>
        <div class="flex flex-col space-y-6 items-center pt-8">
          <!-- Home -->
          <div class="flex flex-col items-center cursor-pointer" @click="selectView('Home')">
            <IconHome
              class="border-[#00ccaa] border-1 w-12 h-12 p-2 rounded-sm"
              :class="activeView == 'Home'
                      ? 'bg-[#64F4D5] text-neutral'
                      : 'bg-neutral text-[#00ccaa]'"
              :selected="activeView == 'Home'"
            />
            <span class="mt-2 text-[#00ccaa] font-medium">Home</span>
          </div>

          <!-- Prompts -->
          <div class="flex flex-col items-center cursor-pointer" @click="selectView('Prompt')">
            <IconPrompt
              class="border-[#00ccaa] border-1 w-12 h-12 p-1 rounded-sm"
              :class="activeView === 'Prompt'
                      ? 'bg-[#64F4D5] text-neutral'
                      : 'bg-neutral text-[#00ccaa]'"
              :selected="activeView === 'Prompt'"
            />
            <span class="mt-2 text-[#00ccaa] font-medium">Prompts</span>
          </div>

          <!-- Models -->
          <div class="flex flex-col items-center cursor-pointer" @click="selectView('Models')">
            <IconModel
              class="border-[#00ccaa] border-1 w-12 h-12 p-2 rounded-sm"
              :class="activeView === 'Models'
                      ? 'bg-[#64F4D5] text-neutral'
                      : 'bg-neutral text-[#00ccaa]'"
              :selected="activeView === 'Models'"
            />
            <span class="mt-2 text-[#00ccaa] font-medium">Models</span>
          </div>

          <!-- Web Apps -->
          <div class="flex flex-col items-center cursor-pointer" @click="selectView('Web Apps')">
            <IconPWA
              class="border-[#00ccaa] border-1 w-12 h-12 p-1 rounded-sm"
              :class="activeView === 'Web Apps'
                      ? 'bg-[#64F4D5] text-neutral'
                      : 'bg-neutral text-[#00ccaa]'"
              :selected="activeView === 'Web Apps'"
            />
            <span class="mt-2 text-[#00ccaa] font-medium">Web Apps</span>
          </div>
           <!-- Products -->
          <div 
          class="flex flex-col items-center cursor-pointer"
            @click="selectView('Products')" >
            <IconProducts
              class="border-[#00ccaa] border-1 w-12 h-12 p-1 rounded-sm"
              :class="activeView === 'Products'
                      ? 'bg-[#64F4D5] text-neutral'
                      : 'bg-neutral text-[#00ccaa]'"
              :selected="activeView === 'Products'"
            />
            <span class="mt-2 text-[#00ccaa] font-medium">Products</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>